/**
 * Componente de Aviso de Quota
 *
 * Exibe avisos quando o usuário está próximo ou atingiu o limite de leads
 *
 * @created 2026-01-19
 * @version 1.0.0
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, AlertCircle, Crown } from 'lucide-react';
import { Link } from 'wouter';
import { authenticatedGet } from '@/utils/api-helpers';

interface QuotaData {
  used: number;
  limit: number;
  remaining: number;
  isPremium: boolean;
  period: string;
  percentUsed: number;
  warningThreshold: number;
  showWarning: boolean;
  message: string;
  status: 'ok' | 'warning' | 'exceeded';
  upgradeUrl: string;
  resetDate: string;
}

interface QuotaWarningProps {
  /**
   * Se true, sempre mostra o contador mesmo sem avisos
   */
  alwaysShow?: boolean;
  /**
   * Classe CSS adicional
   */
  className?: string;
}

/**
 * Hook para buscar dados de quota
 */
export function useQuota() {
  return useQuery<QuotaData>({
    queryKey: ['organizador', 'quota'],
    queryFn: async () => {
      const response = await authenticatedGet('/api/organizador/quota');

      if (!response.ok) {
        throw new Error('Erro ao buscar quota');
      }

      return response.json();
    },
    refetchInterval: 30000, // Atualiza a cada 30 segundos
    staleTime: 10000,
  });
}

/**
 * Componente QuotaWarning
 */
export function QuotaWarning({ alwaysShow = false, className = '' }: QuotaWarningProps) {
  const { data: quota, isLoading, error } = useQuota();

  // Não renderizar nada durante loading ou erro
  if (isLoading || error) {
    return null;
  }

  // Não renderizar se não há dados
  if (!quota) {
    return null;
  }

  // Usuário premium - mostrar badge
  if (quota.isPremium) {
    if (!alwaysShow) return null;

    return (
      <div className={`bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg p-3 ${className}`}>
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Usuário Premium
            </p>
            <p className="text-xs opacity-90">
              {quota.used} leads criados este mês • Ilimitado
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Status: exceeded (limite atingido)
  if (quota.status === 'exceeded') {
    return (
      <div className={`bg-red-50 border-2 border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-red-900 font-semibold text-sm mb-1">
              Limite de leads atingido
            </h3>
            <p className="text-red-700 text-sm mb-3">
              Você atingiu o limite de <strong>{quota.limit} leads/mês</strong>.
              Faça upgrade para o plano premium e crie leads ilimitados.
            </p>
            <div className="flex items-center gap-3">
              <Link href={quota.upgradeUrl}>
                <a className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                  <Crown className="w-4 h-4" />
                  Fazer Upgrade Agora
                </a>
              </Link>
              <p className="text-xs text-red-600">
                Reset em: {new Date(quota.resetDate).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Status: warning (próximo do limite)
  if (quota.status === 'warning' || quota.showWarning) {
    return (
      <div className={`bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-yellow-900 font-semibold text-sm mb-1">
              Atenção: Próximo do limite
            </h3>
            <p className="text-yellow-700 text-sm mb-2">
              Você tem apenas <strong>{quota.remaining} leads restantes</strong> este mês ({quota.percentUsed}% usado).
            </p>
            <div className="mb-3">
              <div className="bg-yellow-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-yellow-600 h-full transition-all duration-500"
                  style={{ width: `${quota.percentUsed}%` }}
                />
              </div>
              <p className="text-xs text-yellow-600 mt-1">
                {quota.used} / {quota.limit} leads usados
              </p>
            </div>
            <Link href={quota.upgradeUrl}>
              <a className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors">
                <Crown className="w-4 h-4" />
                Ver Planos Premium
              </a>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Status: ok - mostrar apenas se alwaysShow
  if (alwaysShow) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700 font-medium">
              Leads este mês
            </p>
            <p className="text-xs text-gray-500">
              {quota.used} / {quota.limit} usados ({quota.percentUsed}%)
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              {quota.remaining}
            </p>
            <p className="text-xs text-gray-500">restantes</p>
          </div>
        </div>
        <div className="mt-2">
          <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-emerald-500 h-full transition-all duration-500"
              style={{ width: `${quota.percentUsed}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Componente compacto para barra de navegação
 */
export function QuotaBadge() {
  const { data: quota, isLoading } = useQuota();

  if (isLoading || !quota) {
    return null;
  }

  if (quota.isPremium) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
        <Crown className="w-3 h-3" />
        Premium
      </div>
    );
  }

  if (quota.status === 'exceeded') {
    return (
      <Link href="/pricing">
        <a className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium hover:bg-red-200 transition-colors">
          <AlertCircle className="w-3 h-3" />
          Limite atingido
        </a>
      </Link>
    );
  }

  if (quota.showWarning) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
        <AlertTriangle className="w-3 h-3" />
        {quota.remaining} restantes
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
      {quota.remaining}/{quota.limit}
    </div>
  );
}

export default QuotaWarning;
